"use client";

import { evaluate, toast } from "@kyan/ui";
import { useDebounceValue } from "usehooks-ts";

import { defaultUnderlying } from "#/config/markets";
import { getPerpConstraints } from "#/config/orders";
import { useTradeButtonFeatureFlag } from "#/hooks/featureFlags/useTradeButtonFeatureFlag";
import { useHedgeSummaryController } from "#/hooks/useHedgeSummary.controller";
import { usePortfolioValidations } from "#/hooks/usePortfolioValidations";
import { useRefetchAccountStateIfStale } from "#/hooks/useRefetchAccountStateIfStale";
import { usePostComboOrder } from "#/services/kyan/hooks/usePostComboOrder";
import { useIsDevMode } from "#/store/app/devMode/devMode";
import { useShowTradeConfirmation } from "#/store/app/portfolio/settings/showTradeConfirmation";
import { useOption } from "#/store/options/tradePanel/option";
import { useOptionLimitPrice } from "#/store/options/tradePanel/optionLimitPrice";
import { useOptionSize } from "#/store/options/tradePanel/optionSize";
import { usePerpLimitPrice } from "#/store/perps/tradePanel/perpLimitPrice";
import { usePerpSize } from "#/store/perps/tradePanel/perpSize";
import { useAccount } from "#/store/wallet/account/account";
import { type ComboTrade, MarketOrder } from "#/types/orders";
import { formatComboError } from "#/utils/formatComboError";
import { createPerpInstrumentName } from "#/utils/instrument.utils";
import { isBuy, toDirection } from "#/utils/marketActionConverter";
import { getMarketByUnderlying } from "#/utils/marketPair";
import { roundToNearest } from "#/utils/number.utils";

import { useMarketTradeButtonController } from "../MarketTradeButton/MarketTradeButton.controller";

export const useHedgeTradeButtonController = (onSuccess?: () => void) => {
  const { option } = useOption();
  const { optionLimitPrice, setOptionLimitPrice } = useOptionLimitPrice();
  const { optionSize, setOptionSize } = useOptionSize();
  const { hasValidImr } = useMarketTradeButtonController();
  const { hedgeDetails } = useHedgeSummaryController();
  const { showTradeConfirmation } = useShowTradeConfirmation();
  const { isDevMode } = useIsDevMode();
  const { account } = useAccount();
  const { refetch } = useRefetchAccountStateIfStale();
  const { setPerpSizeInputValue } = usePerpSize();
  const { setPerpLimitPrice } = usePerpLimitPrice();
  const { isTradeButtonEnabled, tradeButtonTitle } =
    useTradeButtonFeatureFlag();

  const market = getMarketByUnderlying(option?.underlying ?? defaultUnderlying);

  const { isLiquidated, hasZeroEquity } = usePortfolioValidations(market);

  const [debouncedSize] = useDebounceValue(optionSize, 500);
  const [debouncedLimitPrice] = useDebounceValue(optionLimitPrice, 500);

  const limitTotalPremiums = Number(
    (Number(debouncedLimitPrice) * Number(debouncedSize)).toFixed(2),
  );

  const isMissingDetails = !debouncedSize || !debouncedLimitPrice;

  const isValid = Boolean(option);

  const cleanOrderValues = () => {
    setPerpSizeInputValue("");
    setPerpLimitPrice("");
    setOptionSize("");
    setOptionLimitPrice("");
  };

  const { mutate: postComboOrder, isPending } = usePostComboOrder({
    onSuccess: () => {
      toast.success({
        title: "Trade successful",
        description: "∆ Hedged Option Trade",
      });
      onSuccess?.();
      cleanOrderValues();

      refetch?.();
    },
    onError: (error: Error) => {
      toast.error({
        title: "Error with combo order",
        description: formatComboError(error),
      });
    },
  });

  const isDisabled =
    !isTradeButtonEnabled ||
    (!isDevMode &&
      (isMissingDetails || hasZeroEquity || !hasValidImr || isLiquidated));

  const label = evaluate({
    [tradeButtonTitle]: !isTradeButtonEnabled,
    "Liquidation Active": isLiquidated,
    "Enter missing details": isMissingDetails,
    "Insufficient equity": hasZeroEquity,
    "Insufficient Margin": !hasValidImr,
    "Trade with hedge": isValid,
  });
  const buttonLabel = `${label}${isDevMode ? " (Dev)" : ""}`;

  const handleSubmit = () => {
    if (!account || !option || !hedgeDetails) return;

    const exponent = Math.pow(
      10,
      -getPerpConstraints(option.underlying).priceDisplayPrecisionNotional,
    );

    const payload: ComboTrade = {
      limit_total_net_premium: isBuy(option.direction)
        ? -Number(limitTotalPremiums)
        : Number(limitTotalPremiums),
      limit_perp_price: roundToNearest(hedgeDetails.hedgeLimitPrice, exponent),
      market_orders: [
        {
          instrument_name: option.instrument_name,
          direction: option.direction,
          contracts: Number(debouncedSize),
        },
        {
          instrument_name: createPerpInstrumentName(option.underlying),
          direction: toDirection(hedgeDetails.hedgeIsBuy),
          contracts: Number(debouncedSize),
        },
      ],
      taker: account,
      type: MarketOrder.FOK,
    };

    postComboOrder(payload);
  };

  return {
    isLoading: isPending,
    isDisabled,
    buttonLabel,
    showTradeConfirmation,
    handleSubmit,
  };
};
